import { Alert, Button, Card } from "flowbite-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { isValidCNPJ, isValidCPF } from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { ADMIN, QUERIES } from "../../config/apiConfig";
import { HiInformationCircle } from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import usePDF from "../../hooks/usePDF";
import CardQueryDescription from "../common/CardQueryDescription";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import QueryInput from "../inputs/QueryInput";
import { useQuery } from "react-query";
import DrawerLoading from "../DrawerLoading";
import LocalizacaoPFQuery from "./custom-queries-credit/LocalizacaoPFQuery";
import LocalizacaoPJQuery from "./custom-queries-credit/LocalizacaoPJQuery";
import TitleStripe from "../common/TitleStripe";
import ScrBacenQuery from "./custom-queries-credit/ScrBacenQuery";
import { useReactToPrint } from "react-to-print";
import { FaPrint } from "react-icons/fa6";
import AvisoCard from "../common/query/AvisoCard";
import moment from "moment";
import CadastralPFQuery from "./custom-queries-cadastral/CadastralPFQuery";
import CadastralPJQuery from "./custom-queries-cadastral/CadastralPJQuery";
import PreparePrint from "../common/PreparePrint";
import FooterLGPD from "../common/query/FooterLGPD";
import ErrorQuery from "../common/query/ErrorQuery";
import ParticipacaoEmEmpresas from "../common/query/ParticipacaoEmEmpresas";
import ParticipacaoEmEmpresasQuery from "./custom-queries-cadastral/ParticipacaoEmEmpresasQuery";
import CadastralPremiumPFQuery from "./custom-queries-cadastral/CadastralPremiumPFQuery";
import CadastralPremiumPJQuery from "./custom-queries-cadastral/CadastralPremiumPJQuery";
import ValidaPFQuery from "./custom-queries-cadastral/ValidaPFQuery";
import { FaRegFilePdf } from "react-icons/fa";
import ConsultaLoading from "../ConsultaLoading";

const CustomCadastralQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [isDoneRequest, setIsDoneRequest] = useState(false);
  const [error, setError] = useState("");
  const [errorQuery, setErrorQuery] = useState(0);
  const [data, setData] = useState(null);
  const [document, setDocument] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const componentRef = useRef();
  const printRef = useRef();
  const handleAfterPrint = useCallback(() => {
    setIsPrinting(false);
  }, []);

  const handleBeforePrint = useCallback(() => {
    setIsPrinting(true);
    return Promise.resolve();
  }, []);

  const printFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `consulta_${document}`,
    onAfterPrint: handleAfterPrint,
    onBeforePrint: handleBeforePrint,
  });

  const {
    isLoading: isLoadingCustomQuery,
    error: customErrorQuery,
    data: customQuery,
  } = useQuery(["customQuery", query], async () => {
    const { data: items } = await axios.get(
      `${QUERIES.GET_QUERY}/${query?.id}`
    );
    return items.data;
  });

  useEffect(() => {
    setData(null);
    setInfo("");
    setError("");
    setErrorQuery(0);
    setDocument("");
  }, [query]);

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage);
      setDocument(dataFromLocalStorage?.resumoRetorno?.document);
      setIsDoneRequest(true);
    }
  }, [query]);

  const fetchInfo = async (raw) => {
    return await axios(
      `${QUERIES.GET_V2_QUERY}/${customQuery?.url}/${info.replace(/\D/g, "")}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDocument(info);

    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;

    if (customQuery?.QueryCustomized?.parameterType === "CPF") {
      if (!isValidCPF(unmaskedValue)) {
        setError("CPF inválido!");
        isValid = false;
      }
    } else if (customQuery?.QueryCustomized?.parameterType === "CNPJ") {
      if (!isValidCNPJ(unmaskedValue)) {
        setError("CNPJ inválido!");
        isValid = false;
      }
    }

    if (isValid) {
      try {
        setData(null);
        setIsLoading(true);
        setIsDoneRequest(false);
        setError("");
        setErrorQuery(0);

        // Chamada principal: fetchInfo
        const { data: dados } = await fetchInfo();
        setData(dados.data);
        setIsLoading(false);
      } catch (error) {
        const status = error?.response?.status || 500;
        setErrorQuery(status);
        setIsLoading(false);
      } finally {
        setIsDoneRequest(true);
      }
    } else {
      setData(null);
    }
  };

  const renderComponentByKey = (key, dataItem, document) => {
    switch (key) {
      case "cadastralPF":
        return <CadastralPFQuery data={dataItem} />;
      case "cadastralPremiumPF":
        return <CadastralPremiumPFQuery data={dataItem} />;
      case "cadastralPJ":
        return <CadastralPJQuery data={dataItem} />;
      case "cadastralPremiumPJ":
        return <CadastralPremiumPJQuery data={dataItem} />;
      case "participacaoEmEmpresas":
        return <ParticipacaoEmEmpresasQuery data={dataItem} />;
      case "validaPF":
        return <ValidaPFQuery data={dataItem} />;
      default:
        return null;
    }
  };

  const savePDFFn = async () => {
    setIsPrinting(true);
    try {
      const response = await fetchInfo(true);
      const pdfBase64 = response?.data?.data?.resumoRetorno?.pdfBase64;
      const documentId = response?.data?.data?.resumoRetorno?.document;
      if (!pdfBase64) return;
      const byteArray = Uint8Array.from(atob(pdfBase64), (c) =>
        c.charCodeAt(0)
      );
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `consulta_${documentId || "documento"}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setIsPrinting(false);
    }
  };

  const renderSortedData = () => {
    const itemsArray = Object.entries(data)
      .filter(([key, value]) => value && value.prioridade)
      .sort(([, aValue], [, bValue]) => aValue.prioridade - bValue.prioridade);

    return itemsArray.map(([key, dataItem]) => (
      <React.Fragment key={key}>
        {renderComponentByKey(key, dataItem, document)}
      </React.Fragment>
    ));
  };

  return (
    <>
      <PreparePrint isPrinting={isPrinting} />
      <div className="relative">
        <CardQueryDescription
          title="Informações da consulta"
          price={query?.price}
          description={query?.description}
          tags={query?.tags}
        >
          <form
            className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-fit"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col md:flex-row gap-2 items-center z-10">
              <QueryInput
                info={info}
                setInfo={setInfo}
                isLoading={isLoading}
                error={error}
                type={customQuery?.QueryCustomized?.parameterType}
              />
            </div>
          </form>
          <div className="flex gap-2">
            {!errorQuery && data && (
              <Button className="bg-gray-600 hidden md:block" onClick={printFn}>
                <div className="flex gap-2 items-center">
                  <FaPrint className="hidden md:block" />
                  <div className="text-2xs md:text-sm">Imprimir</div>
                </div>
              </Button>
            )}
            {/* {!errorQuery && data && (
              <Button className="primary" onClick={savePDFFn}>
                <div className="flex gap-2 items-center">
                  <FaRegFilePdf className="hidden md:block" />
                  <div className="text-2xs md:text-sm whitespace-nowrap">
                    Salvar PDF
                  </div>
                </div>
              </Button>
            )} */}
          </div>
        </CardQueryDescription>
        {isLoadingCustomQuery && <DrawerLoading />}
      </div>

      {data && (
        <div className="flex flex-col md:flex-row items-center bg-white p-2 gap-2 rounded-md shadow-md mb-4">
          <div className="font-semibold text-sm">
            Deseja realizar uma nova consulta?
          </div>
          <Button
            onClick={() => {
              setData(null);
              setInfo("");
            }}
          >
            Realizar nova consulta
          </Button>
        </div>
      )}

      <SliderToPreview />

      {errorQuery ? (
        <ErrorQuery status={errorQuery} />
      ) : (
        <>
          <div
            className={`overflow-x-scroll print-content mobile-fit ${
              data || isLoading ? "block" : "hidden"
            }`}
            ref={(el) => {
              componentRef.current = el;
              printRef.current = el;
            }}
          >
            {!isLoading && data && (
              <>
                <Card className="card-pdf p-5 relative overflow-hidden">
                  <HeaderQuery title={query?.name} />

                  {customQuery?.QueryCustomized?.showAlertsBoard && (
                    <>
                      <TitleStripe>Quadro de avisos</TitleStripe>
                      <div className="container-alerts three-columns">
                        {data?.serasa && data?.serasa?.statusRetorno == 1 && (
                          <>
                            <AvisoCard
                              title="Pendências - Base I"
                              hasRestrictions={
                                data?.serasa?.pendenciasFinanceiras?.length > 0
                              }
                              messageError={`${data?.serasa?.pendenciasFinanceiras?.length} ocorrências`}
                            />
                            <AvisoCard
                              title="Pendências - Base II"
                              hasRestrictions={
                                data?.serasa?.pendenciasFinanceiras?.length > 0
                              }
                              messageError={`${data?.serasa?.pendenciasFinanceiras?.length} ocorrências`}
                            />
                          </>
                        )}
                        {data?.protestoNacional &&
                          data?.protestoNacional?.statusRetorno == 1 && (
                            <AvisoCard
                              title="Protestos"
                              hasRestrictions={
                                data?.protestoNacional?.protestos?.length > 0
                              }
                              messageError={`${data?.protestoNacional?.protestos?.length} ocorrências`}
                            />
                          )}

                        {data?.chequesSemFundo &&
                          data?.chequesSemFundo?.statusRetorno == 1 && (
                            <AvisoCard
                              title="Cheques Sem Fundo"
                              hasRestrictions={
                                data?.chequesSemFundo?.ocorrencias?.length > 0
                              }
                              messageError={`${data?.chequesSemFundo?.ocorrencias?.length} ocorrências`}
                            />
                          )}
                      </div>
                    </>
                  )}

                  {renderSortedData()}

                  <FooterLGPD />
                </Card>
              </>
            )}
          </div>

          {isLoading && (
            <ConsultaLoading
              fullscreen
              brandName="Relinq"
              accent="brand"
              tips={[
                "Autenticando provedores",
                "Buscando dados das fontes confiáveis",
                "Consolidando informações",
              ]}
            />
          )}
        </>
      )}
    </>
  );
};

export default CustomCadastralQuery;
